import { Link, useLocation } from 'react-router-dom';
import { 
  LogOut, 
  Settings, 
  BarChart3, 
  type LucideIcon,
  Users, 
  Stethoscope, 
  FileText, 
  Calendar, 
  Wand2,
  ChevronDown,
  LayoutDashboard,
  ShieldCheck,
  ClipboardList,
  User,
  UserCheck,
  MessageCircle,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { UserRole } from '@/types/auth';
import { UserRole as UserRoleEnum } from '@/types/auth';
import { useUIStore } from '@/store/useUIStore';
import { BrandMark } from '@/components/common/BrandMark';

type SubItem = {
  name: string;
  href: string;
};

type MenuItem = {
  name: string;
  href: string;
  icon: LucideIcon;
  exact?: boolean;
  subItems?: SubItem[];
};

type SidebarProps = {
  userRole: UserRole;
  userName: string;
  onLogout: () => Promise<void>;
};

const menuItems: Record<UserRoleEnum, MenuItem[]> = {
  [UserRoleEnum.ADMIN]: [
    { name: 'Painel de Controle', href: '/dashboard', icon: LayoutDashboard, exact: true },
    { name: 'Dashboard Corporativo', href: '/admin/analytics', icon: BarChart3 },
    { name: 'Gestão de Médicos', href: '/admin', icon: Stethoscope },
    { name: 'Gestão de Pacientes', href: '/admin?tab=patients', icon: Users },
    { name: 'Aprovação de Médicos', href: '/admin/aprovacao-medicos', icon: UserCheck },
    { name: 'Logs de Auditoria', href: '/admin/audit-logs', icon: ShieldCheck },
    { name: 'Configurações', href: '/settings', icon: Settings },
  ],
  [UserRoleEnum.DOCTOR]: [
    { name: 'Painel de Controle', href: '/dashboard', icon: LayoutDashboard, exact: true },
    { name: 'Minhas Consultas', href: '/consultas', icon: Calendar },
    { name: 'Meus Pacientes', href: '/pacientes', icon: Users },
    { name: 'Criador de Templates', href: '/templates', icon: FileText },
    { name: 'Máscaras', href: '/mascaras', icon: Wand2 },
    { name: 'Minha Agenda', href: '/agenda', icon: ClipboardList },
    { name: 'Receitas e renovações', href: '/fila-renovacoes', icon: ClipboardList },
    { name: 'Configurações', href: '/settings', icon: Settings },
  ],
  [UserRoleEnum.PATIENT]: [
    { name: 'Início', href: '/dashboard', icon: LayoutDashboard, exact: true },
    { name: 'Buscar Médicos', href: '/buscar-medicos', icon: Users },
    { name: 'Médicos que me atenderam', href: '/meus-medicos', icon: MessageCircle },
    { name: 'Minhas Consultas', href: '/minhas-consultas', icon: Calendar },
    { name: 'Meu Prontuário', href: '/prontuario', icon: FileText },
    { name: 'Meus Documentos', href: '/meus-documentos', icon: FileText },
    { name: 'Meu Perfil', href: '/perfil', icon: User },
  ],
};

export function Sidebar({ userRole, userName, onLogout }: SidebarProps) {
  const location = useLocation();
  const { pathname } = location;
  const { sidebarOpen, toggleSidebar } = useUIStore();
  
  // Normalizar o role para garantir que bata com as chaves do menuItems (que são uppercase)
  const normalizedRole = (userRole ? String(userRole).toUpperCase() : '') as UserRoleEnum;
  // Nunca exibir menu admin para roles não-admin (fallback seguro: lista vazia)
  const items =
    normalizedRole && menuItems[normalizedRole]
      ? menuItems[normalizedRole]
      : [];

  const isExactActive = (itemHref: string) => {
    const [path, query] = itemHref.split('?');
    if (query) {
      return pathname === path && location.search.includes(query);
    }
    const siblingOwnsQuery = items.some((other) => {
      if (other.href === itemHref) return false;
      const [otherPath, otherQuery] = other.href.split('?');
      return Boolean(otherQuery) && otherPath === path && location.search.includes(otherQuery);
    });
    return pathname === path && !siblingOwnsQuery;
  };
  
  const isChildActive = (item: MenuItem) => 
    item.subItems?.some(sub => pathname === sub.href) || 
    (pathname.startsWith(item.href + '/') && !isExactActive(item.href));

  return (
    <div 
      className={cn(
        "bg-white dark:bg-neutral-900 border-r border-gray-200 dark:border-neutral-800 h-screen sticky top-0 flex flex-col shadow-lg z-50 transition-[width] duration-300 ease-in-out shrink-0",
        sidebarOpen ? "w-64" : "w-16"
      )}
    >
      {/* Logo */}
      <div className="p-4 border-b border-gray-200 dark:border-neutral-800 flex items-center h-20 shrink-0 relative">
        <div className={cn('flex items-center w-full', sidebarOpen ? 'gap-3' : 'justify-center gap-0')}>
          <BrandMark
            variant={sidebarOpen ? 'horizontal' : 'icon'}
            size={sidebarOpen ? 'lg' : 'md'}
            imageClassName={sidebarOpen ? 'h-12 w-12' : 'h-9 w-9'}
          />
        </div>

        {/* Toggle Button */}
        <button
          onClick={toggleSidebar}
          className={cn(
            "absolute top-1/2 -translate-y-1/2 -right-3 w-6 h-6 bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-full flex items-center justify-center text-gray-500 hover:text-teal-600 hover:border-teal-300 transition-colors z-50 shadow-sm",
            !sidebarOpen && "right-auto left-full translate-x-1"
          )}
          style={!sidebarOpen ? { right: '-12px' } : undefined} // Fix for right positioning when collapsed
          title={sidebarOpen ? "Recolher menu" : "Expandir menu"}
        >
          {sidebarOpen ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
      </div>

      {/* Menu items - Scrollable area */}
      <nav className="flex-1 px-3 py-6 space-y-1.5 overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-gray-200 dark:scrollbar-thumb-neutral-800">
        {items.map((item) => {
          const Icon = item.icon;
          const activeExact = isExactActive(item.href);
          const activeChild = isChildActive(item);
          const isExpanded = (activeExact || activeChild) && sidebarOpen;

          return (
            <div key={item.href} className="flex flex-col space-y-1">
              <Link
                to={item.href}
                title={!sidebarOpen ? item.name : undefined}
                className={cn(
                  'w-full flex items-center py-2 rounded-xl transition-all duration-200 text-[13px] group border-transparent shrink-0',
                  sidebarOpen ? "gap-3 px-3 border-l-4" : "justify-center px-0 border-none gap-0",
                  activeExact
                    ? sidebarOpen ? 'bg-teal-50/80 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400 border-teal-600 font-semibold shadow-xs' : 'text-teal-700 dark:text-teal-400 bg-teal-50/80 dark:bg-teal-900/30'
                    : activeChild
                      ? 'text-teal-700 dark:text-teal-400 font-semibold border-transparent bg-transparent'
                      : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-neutral-800/50 hover:text-gray-900 dark:hover:text-white'
                )}
              >
                <Icon className={cn(
                  "w-5 h-5 flex-shrink-0 transition-transform group-hover:scale-110",
                  (activeExact || activeChild) ? "text-teal-600" : "text-gray-400 dark:text-gray-500"
                )} />
                <span className={cn(
                  "truncate whitespace-nowrap transition-all duration-300",
                  sidebarOpen ? "opacity-100 w-auto" : "opacity-0 w-0 overflow-hidden"
                )}>
                  {item.name}
                </span>
                {item.subItems && sidebarOpen && (
                  <ChevronDown className={cn(
                    "ml-auto w-3.5 h-3.5 transition-transform duration-300", 
                    isExpanded ? "rotate-180 text-teal-600" : "text-gray-400"
                  )} />
                )}
              </Link>
              
              {/* Sub-items */}
              {item.subItems && isExpanded && sidebarOpen && (
                <div className="pl-12 pr-2 space-y-1 mt-1 animate-in slide-in-from-top-2 duration-300 border-l border-gray-100 dark:border-neutral-800 ml-6">
                  {item.subItems.map((sub) => {
                    const isSubActive = pathname === sub.href;
                    return (
                      <Link
                        key={sub.href}
                        to={sub.href}
                        className={cn(
                          'block w-full text-left py-2 px-3 rounded-lg text-xs transition-all border-l-2',
                          isSubActive
                            ? 'bg-teal-50/50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-400 border-teal-500 font-bold shadow-xs'
                            : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-neutral-800 hover:text-gray-900 dark:hover:text-white font-medium border-transparent'
                        )}
                      >
                        {sub.name}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* User section — footer estático sem dropdown */}
      <div
        className={cn(
          "p-3 border-t border-gray-100 dark:border-neutral-800 bg-white dark:bg-neutral-900 shrink-0",
          !sidebarOpen && "flex flex-col items-center p-2"
        )}
        data-testid="sidebar-user-footer"
      >
        {/* Card informativo do usuário */}
        <div className={cn(
          "flex items-center p-2 rounded-2xl mb-2",
          sidebarOpen ? "gap-3" : "justify-center p-0 mb-3 gap-0"
        )}>
          <div className="relative shrink-0">
            <div className="w-8 h-8 rounded-xl bg-linear-to-br from-teal-500 to-teal-600 flex items-center justify-center text-white font-bold text-sm shadow-sm">
              {(userName || 'U').charAt(0).toUpperCase()}
            </div>
            <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 border-2 border-white dark:border-neutral-900 rounded-full" />
          </div>
          
          <div className={cn(
            "min-w-0 text-left whitespace-nowrap transition-all duration-300 overflow-hidden",
            sidebarOpen ? "opacity-100 w-auto flex-1" : "opacity-0 w-0"
          )}>
            <p className="text-sm font-bold text-gray-900 dark:text-white truncate leading-tight">
              {(userName || 'Usuário').split(' ')[0]}
            </p>
            <p className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-tighter">
              {userRole}
            </p>
          </div>
        </div>

        {/* Botão de logout */}
        <button
          data-testid="sidebar-logout-button"
          onClick={onLogout}
          title={!sidebarOpen ? "Sair do Sistema" : undefined}
          className={cn(
            "flex items-center py-2 text-sm text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30 rounded-xl transition-colors font-semibold group",
            sidebarOpen ? "w-full gap-3 px-3" : "justify-center px-0 gap-0"
          )}
        >
          <LogOut className="w-4 h-4 shrink-0 group-hover:-translate-x-1 transition-transform" />
          <span className={cn(
            "whitespace-nowrap transition-all duration-300",
            sidebarOpen ? "opacity-100 w-auto" : "opacity-0 w-0 overflow-hidden"
          )}>
            Sair do Sistema
          </span>
        </button>
      </div>
    </div>
  );
}
